import { useMutation, useQueryClient } from "@tanstack/react-query";
import { reportMutations } from "../../../../mutations";
import { reportQueries } from "../../../../queries";
import type { AnalysisStepRow } from "@/types/supabase.types";

interface DeleteReportStepParams {
  stepId: string;
  reportId: string;
}

export function useDeleteReportStep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ stepId }: DeleteReportStepParams) => {
      const { error } = await reportMutations.analysisStep.delete(stepId);
      if (error) throw error;
      return { stepId };
    },
    onMutate: async ({ stepId, reportId }) => {
      const stepsQueryKey = reportQueries.steps(reportId).queryKey;
      await queryClient.cancelQueries({ queryKey: stepsQueryKey });

      const previous =
        queryClient.getQueryData<AnalysisStepRow[]>(stepsQueryKey);

      // Optimistically remove the step
      if (previous) {
        queryClient.setQueryData<AnalysisStepRow[]>(stepsQueryKey, (old) =>
          (old ?? []).filter((s) => s.analysis_step_id !== stepId),
        );
      }

      return { previous, stepsQueryKey };
    },
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.stepsQueryKey, context.previous);
      }
    },
    onSettled: async (_data, _error, { reportId }) => {
      await Promise.all([
        queryClient.invalidateQueries(reportQueries.stepsKey()),
        queryClient.invalidateQueries(reportQueries.detail(reportId)),
      ]);
    },
  });
}
